import { Address, NotificationOrigin } from '@synergy/models';
import { ApiResult, PaginatedResponse, request, safeRequest } from '@synergy/api';
import { createSearchByObject } from '@synergy/utils';

import { Account } from '../models/account';
import { Contact } from '../models/contact';
import { TermsAndConditions } from '../models/terms-and-conditions';
import { TermsAndConditionsResponse } from './models/terms-and-conditions-response';

/** How to reach a company — what the contact card in a list shows (#2229). */
export interface AccountContactInfo {
    id: number;
    name: string;
    mainPhone?: string;
    mainEmail?: string;
    mainContactId?: number;
    mainContactName?: string;
    mainContactPhone?: string;
    mainContactEmail?: string;
}

/** A notification of an account that is sent to an address of its own. */
export interface NotificationOverride {
    id: number;
    notificationOrigin: NotificationOrigin;
    recipientEmail: string;
}

/** A price info sent to a member; see GetAccountPriceInfosQueryHandler. */
export interface AccountPriceInfo {
    mediaId: number;
    inquiryId: number;
    inquiryNumber: string;
    inquiryTitle: string | null;
    sentAt: string;
    responsibleContactName: string | null;
}

/** A bonus settlement a member or a supplier took part in. */
export interface AccountBonusSettlement {
    bonusSettlementId: number;
    supplierBonusSettlementId: number | null;
    year: number;
    type: string;
    status: string;
    amount: number | null;
    hasBonusPayout: boolean | null;
    date: string;
}

export class AccountApi {
    constructor(protected baseUrl: string) {}

    public cget(
        params: URLSearchParams = new URLSearchParams(),
        abortSignal?: AbortSignal
    ): Promise<ApiResult<PaginatedResponse<Account>>> {
        return safeRequest(
            () => request.get(`${this.baseUrl}/accounts`, { params, signal: abortSignal }),
            response => {
                response.data.items = response.data.items.map(Account.fromPlainObject);
                return response.data;
            }
        );
    }

    public getAddresses(
        accountId: number,
        search?: string,
        useFromParent?: boolean,
        abortSignal?: AbortSignal
    ): Promise<ApiResult<Address[]>> {
        const params = createSearchByObject({ search, useFromParent, limit: 40 });

        return safeRequest<Address[], PaginatedResponse<Address>>(
            () => request.get(`${this.baseUrl}/accounts/${accountId}/addresses`, { params, signal: abortSignal }),
            response => response.data.items.map(Address.fromPlainObject)
        );
    }

    public getContacts(
        poolAccountId: number,
        search?: string,
        abortSignal?: AbortSignal
    ): Promise<ApiResult<Contact[]>> {
        const params = createSearchByObject({ accountId: poolAccountId, isLocked: 'false', search, limit: 40 });
        return safeRequest<Contact[], PaginatedResponse<Contact>>(
            () => request.get(`${this.baseUrl}/quicksearch/contacts`, { params, signal: abortSignal }),
            response => response.data.items.map(Contact.fromPlainObject)
        );
    }

    public getPriceInfos(
        accountId: number,
        params: URLSearchParams,
        abortSignal?: AbortSignal
    ): Promise<ApiResult<PaginatedResponse<AccountPriceInfo>>> {
        return safeRequest(
            () => request.get(`${this.baseUrl}/accounts/${accountId}/price-infos`, { params, signal: abortSignal }),
            response => response.data
        );
    }

    public getBonusSettlements(
        accountId: number,
        side: 'customer' | 'supplier',
        params: URLSearchParams,
        abortSignal?: AbortSignal
    ): Promise<ApiResult<PaginatedResponse<AccountBonusSettlement>>> {
        const withSide = new URLSearchParams(params);
        withSide.set('side', side);

        return safeRequest(
            () =>
                request.get(`${this.baseUrl}/accounts/${accountId}/bonus-settlements`, {
                    params: withSide,
                    signal: abortSignal,
                }),
            response => response.data
        );
    }

    /** Read when a contact card opens, not for the rows of a list (LST-S13). */
    public getContactInfo(accountId: number, abortSignal?: AbortSignal): Promise<ApiResult<AccountContactInfo>> {
        return safeRequest(
            () =>
                request.get<AccountContactInfo>(`${this.baseUrl}/accounts/${accountId}/contact-info`, {
                    signal: abortSignal,
                }),
            response => response.data
        );
    }

    /** Where this account's notifications go, where that is not the usual address. */
    public getNotificationOverrides(accountId: number): Promise<ApiResult<NotificationOverride[]>> {
        return safeRequest(
            () =>
                request.get<{ overrides: NotificationOverride[] }>(
                    `${this.baseUrl}/accounts/${accountId}/notification-overrides`
                ),
            response => response.data.overrides
        );
    }

    public getBonusConditions(accountId: number): Promise<ApiResult<TermsAndConditions>> {
        return safeRequest(
            () => request.get<TermsAndConditionsResponse>(`${this.baseUrl}/accounts/${accountId}/conditions`),
            response => {
                return {
                    bonusConditions: response.data.bonusConditions?.join('\r\n') ?? '',
                    termsOfDelivery: response.data.termsOfDelivery,
                    paymentTerm: response.data.paymentTerm,
                    bonus: response.data.bonus ?? [],
                };
            }
        );
    }
}
